import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import bemClass from '@upsales/components/Utils/bemClass';
import Text from '@upsales/components/Text';
import Icon from '@upsales/components/Icon';
import { Flex } from '@upsales/components';
import JourneyHistoryDropDown from './JourneyHistoryDropDown';
import { statuses, getJourneyStep, statusDisqualifiable, LEAD } from 'Components/Helpers/journeyStep';
import JourneyStepHistory from 'Resources/JourneyStepHistory';
import logError from 'App/babel/helpers/logError';

import './JourneyStepDisplay.scss';
import { isAvailable, Feature } from 'App/services/FeatureHelperStandalone';

const getIconName = (isExpanded, saving) => {
	if (saving) {
		return 'spinner';
	} else {
		return isExpanded ? 'chevron-up' : 'chevron-down';
	}
};

class JourneyStepDisplay extends PureComponent {
	state = { saving: false, historyArr: [] };
	lang = {
		updatingStatus: Tools.$translate('default.updatingStatus'),
		disqualify: Tools.$translate('default.disqualify'),
		disqualifyCompany: Tools.$translate('default.disqualifyCompany'),
		disqualifyContact: Tools.$translate('default.disqualifyContact'),
		salesQualifyCompany: Tools.$translate('default.salesQualifyCompany'),
		salesQualifyContact: Tools.$translate('default.salesQualifyContact')
	};

	fetchHistory = force => {
		const { id, type, journeyStep } = this.props;
		if (id && (!this.historyFetched || force)) {
			this.historyFetched = true;
			JourneyStepHistory.find(id, type)
				.then(historyArr => {
					if (!historyArr || !historyArr.length) {
						historyArr = [{ value: journeyStep || LEAD, date: null }];
					}
					this.setState({ historyArr });
				})
				.catch(e => {
					logError(e, 'Failed fetching journey history', { id, type });
				});
		}
	};

	onSetJourneyStep = journeyStep => {
		this.setState({ saving: true });
		return this.props.onSetJourneyStep(journeyStep).finally(() => {
			this.setState({ saving: false });
			this.fetchHistory(true);
		});
	};

	componentDidUpdate(prevProps) {
		if (this.props.journeyStep !== prevProps.journeyStep) {
			this.fetchHistory(true);
		}
	}

	render() {
		if (!isAvailable(Feature.JOURNEY_STATUS)) {
			return null;
		}

		const { journeyStep, className, type, disableActions, isSubaccount, isInactive } = this.props;
		const { saving } = this.state;
		const classes = new bemClass('JourneyStepDisplay', className);
		const step = getJourneyStep(journeyStep || LEAD);
		const tooltipArray = [
			type === 'client' ? this.lang.salesQualifyCompany : this.lang.salesQualifyContact,
			type === 'client' ? this.lang.disqualifyCompany : this.lang.disqualifyContact
		];

		return (
			<div className={classes.mod({ isSubaccount, isInactive }).b()}>
				<JourneyHistoryDropDown
					disableActions={disableActions}
					onSetJourneyStep={this.onSetJourneyStep}
					tooltipArray={tooltipArray}
					journeyStep={journeyStep || LEAD}
					historyArr={this.state.historyArr} // If current and journeyStep missmatch
				>
					{(isExpanded, setExpanded) => (
						<Flex
							className={classes.elem('button-wrapper').mod({ expanded: isExpanded }).b()}
							onClick={setExpanded}
							onMouseOver={() => this.fetchHistory(false)}
							justifyContent="space-between"
							alignItems="center"
						>
							<Flex direction="column">
								<Text size="sm" bold space="mbs">
									{type === 'client'
										? Tools.$translate('default.journeyStepClient')
										: Tools.$translate('default.journeyStepContact')}
								</Text>
								<Text size="sm" color={isExpanded ? 'grey-11' : 'bright-blue'}>
									{Tools.$translate(step.name)}
								</Text>
							</Flex>
							<Icon
								className={classes.elem('button').mod({ open: isExpanded }).b()}
								spin={saving}
								name={getIconName(isExpanded, saving)}
								color={isExpanded ? 'grey-10' : 'grey-11'}
							/>
						</Flex>
					)}
				</JourneyHistoryDropDown>
			</div>
		);
	}
}

JourneyStepDisplay.statusDisqualifiable = statusDisqualifiable;

JourneyStepDisplay.defaultProps = {
	type: 'client',
	journeyStep: LEAD,
	isSubaccount: false,
	isInactive: false
};

JourneyStepDisplay.propTypes = {
	id: PropTypes.number.isRequired,
	type: PropTypes.oneOf(['client', 'contact']).isRequired,
	journeyStep: PropTypes.oneOf(statuses),
	disableActions: PropTypes.bool,
	className: PropTypes.string,
	onSetJourneyStep: PropTypes.func,
	isSubaccount: PropTypes.bool,
	isInactive: PropTypes.bool
};

export default JourneyStepDisplay;
